import { ReactNode, useMemo } from 'react';

import { useGetAllInventorySites } from '@restDataAccess';

import { FeedbackAlert } from '@/components/feedbackAlert/FeedbackAlert.tsx';
import { useConfigStore, useDevSettingsStore } from '@/stores';
import { configLoadFailedFeedbackProps } from '@/utils/config.ts';

import { AccessibleSitesContext } from './accessibleSitesProvider/AccessibleSitesContext.ts';
import { buildSitesApis } from './accessibleSitesProvider/siteProvider/buildSitesApis.ts';

type AccessibleSitesProviderProps = {
  children: ReactNode;
};

/**
 * Sole owner of the accessible-site list, mounted once above {@link SiteProvider}. Every
 * `SiteProvider` — the app-level one plus each nested `persist` instance behind a dashboard widget
 * or config panel — reads the list from this context, so the site-inventory query keeps a
 * single subscriber however many site-scoped subtrees are mounted.
 *
 * Also gates the site stack on `/config`, which must be loaded before {@link useGetApiUrl} can
 * resolve a base URL for that query.
 */
export const AccessibleSitesProvider = ({ children }: AccessibleSitesProviderProps) => {
  const loaded = useConfigStore(s => s.loaded);
  const error = useConfigStore(s => s.error);

  if (!loaded) {
    return <FeedbackAlert loading />;
  }

  if (error) {
    return <FeedbackAlert {...configLoadFailedFeedbackProps(error)} />;
  }

  return <AccessibleSitesWithConfig>{children}</AccessibleSitesWithConfig>;
};

/** Separate component so the site query mounts only once `/config` has supplied `defaultSite` / `env`. */
const AccessibleSitesWithConfig = ({ children }: AccessibleSitesProviderProps) => {
  const defaultSite = useConfigStore(s => s.defaultSite);
  const env = useConfigStore(s => s.env);
  const portableMode = useConfigStore(s => s.portableMode);
  const forceScaleInternal = useDevSettingsStore(s => s.forceScaleInternal);

  const { data: globalInventorySites, isFetched: areSitesResolved } = useGetAllInventorySites();

  const sites = useMemo(
    () =>
      buildSitesApis({
        defaultSite,
        globalInventorySites,
        env,
        portableMode,
        forceScaleInternal,
      }),
    [defaultSite, env, forceScaleInternal, portableMode, globalInventorySites]
  );
  const value = useMemo(() => ({ areSitesResolved, sites }), [areSitesResolved, sites]);

  return (
    <AccessibleSitesContext.Provider value={value}>{children}</AccessibleSitesContext.Provider>
  );
};
